import type { Attachment } from "@catmail/shared";
import { CloudDownload, FileText, FileX2 } from "lucide-react";

import { cn } from "@/lib/cn";
import { INTERACTIVE } from "@/lib/interaction";
import { formatBytes } from "@/lib/datetime";

type AttachmentCardProps = {
  attachment: Attachment;
  onOpen?: (attachment: Attachment) => void;
  /** 배치만 — 대화 안에서는 고정 폭, 목록에서는 꽉 채운다. (react-component-rules §7) */
  className?: string;
};

export function AttachmentCard({
  attachment,
  onOpen,
  className,
}: AttachmentCardProps) {
  /**
   * 내용을 받지 못한 첨부. 너무 커서 잘렸거나 파일이 사라진 경우다.
   *
   * 이름과 크기는 메일에 적혀 있어서 보여줄 수 있지만 내려받을 것이 없다.
   * 예전에는 멀쩡한 카드와 똑같이 생긴 채로 눌러도 아무 일이 없었다 — 사용자
   * 눈에는 앱이 고장난 것이지 파일이 없는 것이 아니다.
   */
  const missing = attachment.downloadUrl.length === 0;

  return (
    <button
      type="button"
      onClick={() => onOpen?.(attachment)}
      disabled={missing}
      title={missing ? "내용을 가져오지 못한 첨부예요" : undefined}
      className={cn(
        "flex w-65 items-center gap-2.5 rounded-[14px] border px-4 py-3 text-left",
        missing
          ? "border-line-soft bg-surface-2 cursor-default"
          : `bg-surface border-line-bubble hover:bg-paper/50 active:bg-chip ${INTERACTIVE}`,
        className,
      )}
    >
      <span
        className={cn(
          "flex size-9.5 shrink-0 items-center justify-center rounded-[10px]",
          missing ? "bg-surface-3" : "bg-sunken",
        )}
      >
        {/* 5MiB를 넘겨 링크로 나간 첨부는 아이콘부터 다르게 보인다 */}
        {missing ? (
          <FileX2 className="text-muted-3 size-4.5" />
        ) : attachment.offloaded ? (
          <CloudDownload className="text-body size-4.5" />
        ) : (
          <FileText className="text-body size-4.5" />
        )}
      </span>

      <span className="flex min-w-0 flex-col gap-0.5">
        <span
          className={cn(
            "line-clamp-1-ko text-sm",
            missing ? "text-muted" : "text-ink",
          )}
        >
          {attachment.filename}
        </span>
        <span className={cn("text-xs", missing ? "text-muted-3" : "text-muted-2")}>
          {missing
            ? "내용을 가져오지 못했어요"
            : `${formatBytes(attachment.size)}${attachment.offloaded ? " · 링크로 전송됨" : ""}`}
        </span>
      </span>
    </button>
  );
}
